import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../bom/ui/Toast';
import { sbomApi } from './sbomApi';

const TRIGGER = { manual: 'Check now', daily: 'Daily', import: 'After import' };
const when = (s) => (s ? `${String(s).slice(0, 16)} UTC` : '—');

/** Name (and note) a check to keep it for good. Used from the history list and the open check's banner. */
export function SaveCheckForm({ check, onSaved, onCancel }) {
  const { showToast } = useToast();
  const [name, setName] = useState(check.saved_name || `Check of ${String(check.finished_at || '').slice(0, 10)}`);
  const [note, setNote] = useState(check.saved_note || '');
  const [busy, setBusy] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    sbomApi.saveCheck(check.id, name.trim(), note.trim())
      .then(() => { showToast(`Saved "${name.trim()}"`, 'success'); onSaved(); })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setBusy(false));
  };
  return (
    <form className="sbom-savecheck" onSubmit={submit}>
      <label className="sbom-tiny" htmlFor={`sc-name-${check.id}`}>Name</label>
      <input id={`sc-name-${check.id}`} className="sbom-input" value={name} maxLength={120} autoFocus
        onChange={(e) => setName(e.target.value)} />
      <label className="sbom-tiny" htmlFor={`sc-note-${check.id}`}>Note (optional)</label>
      <input id={`sc-note-${check.id}`} className="sbom-input" value={note} maxLength={2000}
        placeholder="e.g. Evidence for the 2.4 release" onChange={(e) => setNote(e.target.value)} />
      <span className="sbom-actions">
        <button type="submit" className="sbom-btn sbom-btn--pri" disabled={busy || !name.trim()}>{check.saved ? 'Rename' : 'Save'}</button>
        <button type="button" className="sbom-btn sbom-btn--ghost" onClick={onCancel}>Cancel</button>
      </span>
    </form>
  );
}

/**
 * Vulnerability check history — every finished check (newest first) with what it found and how that
 * differed from the check before. Open shows that check read-only on the Vulnerabilities page; Save
 * keeps it for good (unsaved checks are pruned to the newest `keep`).
 */
export default function SbomCheckHistory({ current, onOpen, onClose, onAuth }) {
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [savedOnly, setSavedOnly] = useState(false);
  const [saving, setSaving] = useState(null);     // check id whose save form is open
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let off = false;
    sbomApi.checks(savedOnly)
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off && !onAuth(e)) setData({ error: e.message }); });
    return () => { off = true; };
  }, [savedOnly, tick, onAuth]);

  const reload = useCallback(() => { setSaving(null); setTick((t) => t + 1); }, []);
  const unsave = (c) => sbomApi.unsaveCheck(c.id)
    .then(() => { showToast(`"${c.saved_name}" is no longer saved`, 'success'); reload(); })
    .catch((e) => showToast(e.message, 'error'));

  return (
    <div className="sbom-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="sbom-modal" style={{ width: 'min(820px, 100%)' }} role="dialog" aria-modal="true" aria-labelledby="sbom-ch-title">
        <div className="sbom-modal-head">
          <div>
            <h3 id="sbom-ch-title">Check history</h3>
            <div className="sbom-tiny sbom-muted">Every check is kept with what it found. Save one to keep it for good
              {data?.keep ? ` — unsaved checks are kept for the last ${data.keep}` : ''}.</div>
          </div>
          <button type="button" className="sbom-modal-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="sbom-modal-body">
          <div className="sbom-tabs" role="group" aria-label="Show">
            <button type="button" className={!savedOnly ? 'sbom-on' : ''} aria-pressed={!savedOnly} onClick={() => setSavedOnly(false)}>All checks</button>
            <button type="button" className={savedOnly ? 'sbom-on' : ''} aria-pressed={savedOnly} onClick={() => setSavedOnly(true)}>Saved</button>
          </div>
          {data?.error && <div className="sbom-error" role="alert">{data.error}</div>}
          {!data && <div className="sbom-empty">Loading…</div>}
          {data?.checks && data.checks.length === 0 && (
            <div className="sbom-empty">{savedOnly ? 'No saved checks yet.' : 'No checks yet. Press Check now.'}</div>
          )}
          {data?.checks && data.checks.length > 0 && (
            <div className="sbom-tw">
              <table className="sbom-table">
                <thead>
                  <tr><th>Checked</th><th className="sbom-hide-s">Started by</th><th>Findings</th><th>Change</th><th>Saved as</th><th /></tr>
                </thead>
                <tbody>
                  {data.checks.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr className={String(current) === String(c.id) ? 'sbom-row--located' : ''}>
                        <td className="sbom-mono">{when(c.finished_at)}</td>
                        <td className="sbom-hide-s">{TRIGGER[c.trigger] || c.trigger}{c.started_by ? <span className="sbom-muted"> · {c.started_by}</span> : ''}</td>
                        <td>
                          {c.ok ? (
                            <>{c.counts.all ?? 0}
                              {c.counts.kev > 0 && <span className="sbom-kev" title="Actively exploited (CISA KEV)">KEV {c.counts.kev}</span>}
                              {c.counts.critical > 0 && <span className="sbom-tiny sbom-muted"> · {c.counts.critical} critical/high</span>}</>
                          ) : <span className="sbom-tiny sbom-badtext" title={c.error}>Failed — nothing recorded</span>}
                        </td>
                        <td className="sbom-tiny">
                          {c.ok ? (
                            <>{c.stats.new > 0 ? <span className="sbom-pill sbom-pill--info">+{c.stats.new} new</span> : null}
                              {c.stats.gone > 0 ? <span className="sbom-pill sbom-pill--ok" style={{ marginLeft: 4 }}>−{c.stats.gone} gone</span> : null}
                              {!c.stats.new && !c.stats.gone ? <span className="sbom-muted">No change</span> : null}</>
                          ) : <span className="sbom-muted">—</span>}
                        </td>
                        <td>{c.saved ? (
                          <span title={[c.saved_note, `Saved ${when(c.saved_at)}${c.saved_by ? ` by ${c.saved_by}` : ''}`].filter(Boolean).join('\n')}>
                            ★ {c.saved_name}</span>
                        ) : <span className="sbom-muted">—</span>}</td>
                        <td className="sbom-actions" style={{ flexWrap: 'nowrap' }}>
                          {c.ok && <button type="button" className="sbom-btn" onClick={() => onOpen(c.id)}>Open</button>}
                          {c.ok && (
                            <button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => setSaving(saving === c.id ? null : c.id)}>
                              {c.saved ? 'Rename' : 'Save'}</button>
                          )}
                          {c.saved && <button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => unsave(c)}>Unsave</button>}
                        </td>
                      </tr>
                      {saving === c.id && (
                        <tr><td colSpan={6}><SaveCheckForm check={c} onSaved={reload} onCancel={() => setSaving(null)} /></td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="sbom-modal-foot">
          <button type="button" className="sbom-btn" onClick={onClose}>Close</button>
        </div>
      </section>
    </div>
  );
}
